import { RefObject, useRef } from "react";
import { useReactiveVar } from "@apollo/client";
import { robotsState } from "../../state";
import {
  BundleMode,
  BundleNames,
} from "../../../../MobileAppModal/MobileAppModelModal/constants";
import {
  CurrentBundleContainer,
  MobileAppModalBundleName,
} from "../../../../MobileAppModal/MobileAppModal.style";
import {
  ButtonCustom,
  ImportFileUpload,
  BundleLink,
} from "../../../../shared/Style/Style";
import { IconButton } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import { GameModelStateFileTypeEnum } from "../../../../../generated/graphql";
import { BundleTypesMap } from "../../maps";

export const StageBundleBlock = () => {
  const iosInput = useRef<HTMLInputElement | null>(null);
  const iosInputOptimized = useRef<HTMLInputElement | null>(null);
  const androidInput = useRef<HTMLInputElement | null>(null);
  const androidInputOptimized = useRef<HTMLInputElement | null>(null);
  const webGlInput = useRef<HTMLInputElement | null>(null);
  const webGlInputOptimized = useRef<HTMLInputElement | null>(null);
  const manifestInputIos = useRef<HTMLInputElement | null>(null);
  const manifestInputIosOptimized = useRef<HTMLInputElement | null>(null);
  const manifestInputAndroid = useRef<HTMLInputElement | null>(null);
  const manifestInputAndroidOptimized = useRef<HTMLInputElement | null>(null);
  const manifestInputWebGl = useRef<HTMLInputElement | null>(null);
  const manifestInputWebGlOptimized = useRef<HTMLInputElement | null>(null);

  const currentBundles = useReactiveVar(robotsState.stageBundles);
  const stageGeneralInfo = useReactiveVar(robotsState.stageGeneralInfo);
  const isLoading = useReactiveVar(robotsState.isLoading);

  const uploadStageBundle = async (
    file: File,
    fileType: GameModelStateFileTypeEnum
  ) => {
    if (!file) return;

    await robotsState.updateRobotStageFile({
      updateGameModelStateFileId: stageGeneralInfo.id,
      file,
      fileType,
    });

    await robotsState.getRobotStage({
      getGameModelStateId: stageGeneralInfo?.id,
    });
  };

  const deleteStageBundle = async (fileType: GameModelStateFileTypeEnum) => {
    await robotsState.deleteRobotStageFile({
      deleteGameModelStateFileId: stageGeneralInfo.id,
      fileType,
    });

    await robotsState.getRobotStage({
      getGameModelStateId: stageGeneralInfo?.id,
    });
  };

  const bundlesData = [
    {
      name: BundleNames.Ios,
      currentBundle: currentBundles?.iosBundle,
      type: BundleMode.Bundle,
      inputRef: iosInput,
      handleBundle: (file: File) =>
        uploadStageBundle(file, GameModelStateFileTypeEnum.IosBundle),
    },
    {
      name: BundleNames.IosOptimized,
      currentBundle: currentBundles?.iosBundleOptimized,
      type: BundleMode.Bundle,
      inputRef: iosInputOptimized,
      handleBundle: (file: File) =>
        uploadStageBundle(file, GameModelStateFileTypeEnum.IosBundleOptimized),
    },
    {
      name: BundleNames.Android,
      currentBundle: currentBundles?.androidBundle,
      type: BundleMode.Bundle,
      inputRef: androidInput,
      handleBundle: (file: File) =>
        uploadStageBundle(file, GameModelStateFileTypeEnum.AndroidBundle),
    },
    {
      name: BundleNames.AndroidOptimized,
      currentBundle: currentBundles?.androidBundleOptimized,
      type: BundleMode.Bundle,
      inputRef: androidInputOptimized,
      handleBundle: (file: File) =>
        uploadStageBundle(
          file,
          GameModelStateFileTypeEnum.AndroidBundleOptimized
        ),
    },
    {
      name: BundleNames.WebGL,
      currentBundle: currentBundles?.webGlBundle,
      type: BundleMode.Bundle,
      inputRef: webGlInput,
      handleBundle: (file: File) =>
        uploadStageBundle(file, GameModelStateFileTypeEnum.WebGlBundle),
    },
    {
      name: BundleNames.WebGlOptimized,
      currentBundle: currentBundles?.webGlBundleOptimized,
      type: BundleMode.Bundle,
      inputRef: webGlInputOptimized,
      handleBundle: (file: File) =>
        uploadStageBundle(file, GameModelStateFileTypeEnum.WebGlBundleOptimized),
    },
    {
      name: BundleNames.IosManifest,
      currentBundle: currentBundles?.iosManifest,
      type: BundleMode.Manifest,
      inputRef: manifestInputIos,
      handleBundle: (file: File) =>
        uploadStageBundle(file, GameModelStateFileTypeEnum.IosManifest),
    },
    {
      name: BundleNames.IosOptimizedManifest,
      currentBundle: currentBundles?.iosManifestOptimized,
      type: BundleMode.Manifest,
      inputRef: manifestInputIosOptimized,
      handleBundle: (file: File) =>
        uploadStageBundle(
          file,
          GameModelStateFileTypeEnum.IosManifestOptimized
        ),
    },
    {
      name: BundleNames.AndroidManifest,
      currentBundle: currentBundles?.androidManifest,
      type: BundleMode.Manifest,
      inputRef: manifestInputAndroid,
      handleBundle: (file: File) =>
        uploadStageBundle(file, GameModelStateFileTypeEnum.AndroidManifest),
    },
    {
      name: BundleNames.AndroidOptimizedManifest,
      currentBundle: currentBundles?.androidManifestOptimized,
      type: BundleMode.Manifest,
      inputRef: manifestInputAndroidOptimized,
      handleBundle: (file: File) =>
        uploadStageBundle(
          file,
          GameModelStateFileTypeEnum.AndroidManifestOptimized
        ),
    },
    {
      name: BundleNames.WebGlManifest,
      currentBundle: currentBundles?.webGlManifest,
      type: BundleMode.Manifest,
      inputRef: manifestInputWebGl,
      handleBundle: (file: File) =>
        uploadStageBundle(file, GameModelStateFileTypeEnum.WebGlManifest),
    },
    {
      name: BundleNames.WebGlOptimizedManifest,
      currentBundle: currentBundles?.webGlManifestOptimized,
      type: BundleMode.Manifest,
      inputRef: manifestInputWebGlOptimized,
      handleBundle: (file: File) =>
        uploadStageBundle(
          file,
          GameModelStateFileTypeEnum.WebGlManifestOptimized
        ),
    },
  ];

  const renderCurrentBundle = (
    name: BundleNames,
    currentBundle: string,
    type: BundleMode,
    inputRef: RefObject<HTMLInputElement>,
    handleBundle: (file: File) => void
  ) => {
    const label = type === BundleMode.Manifest ? `${name}` : `${name} ${type}`;
    const fileType = BundleTypesMap.get(name);

    return (
      <CurrentBundleContainer key={name}>
        <MobileAppModalBundleName>{name}</MobileAppModalBundleName>

        {!!currentBundle ? (
          <span>
            <BundleLink href={currentBundle}>{label}</BundleLink>

            <IconButton
              disabled={isLoading}
              onClick={() => deleteStageBundle(fileType)}
            >
              <ClearIcon color="error" fontSize="small" />
            </IconButton>
          </span>
        ) : (
          <ButtonCustom
            disabled={isLoading}
            onClick={() => inputRef.current.click()}
          >
            <span>Прикріпити</span>
            <ImportFileUpload
              type="file"
              ref={inputRef}
              onChange={({ target: { files } }) =>
                files[0] && handleBundle(files[0])
              }
            />
          </ButtonCustom>
        )}
      </CurrentBundleContainer>
    );
  };

  if (!currentBundles) return null;

  return (
    <>
      {bundlesData.map(
        ({ name, currentBundle, type, inputRef, handleBundle }) =>
          renderCurrentBundle(name, currentBundle, type, inputRef, handleBundle)
      )}
    </>
  );
};
